import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  Gamepad2, Trophy, Plus, Trash2, Save, Loader2, GripVertical,
  RefreshCw, ChevronUp, ChevronDown,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface Tier {
  id?: string;
  tierName: string;
  minScore: number;
  maxScore: number;
  discountType: 'fixed' | 'percent';
  discountValue: number;
  sortOrder: number;
}

interface LeaderboardEntry {
  firstName: string;
  score: number;
  claimCount: number;
}

interface StoreOption {
  id: string;
  nickname: string;
  storeUrl: string;
}

function emptyTier(sortOrder: number): Tier {
  return {
    tierName: '',
    minScore: 0,
    maxScore: 999,
    discountType: 'percent',
    discountValue: 10,
    sortOrder,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GamePopup() {
  const authOpts = useAuthHeaders();
  const token = useAuthStore((s) => s.token);
  const { toast } = useToast();

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [activeTab, setActiveTab] = useState<'tiers' | 'leaderboard'>('tiers');

  // Tiers state
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Typed helper to avoid TS errors from the union type in useAuthHeaders()
  const authHeaders = authOpts.headers as Record<string, string>;

  // Load stores on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/stores`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data: any[]) => {
        const opts = data.map((s) => ({ id: s.id, nickname: s.nickname || s.storeUrl, storeUrl: s.storeUrl }));
        setStores(opts);
        if (opts.length > 0) setSelectedStoreId(opts[0].id);
      })
      .catch(() => {});
  }, [token]);

  // Load tiers when store changes
  useEffect(() => {
    if (!selectedStoreId || !token) return;
    setLoadingTiers(true);
    fetch(`${BASE}/api/game/tiers?storeId=${selectedStoreId}`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setTiers(data.tiers || []))
      .catch(() => {})
      .finally(() => setLoadingTiers(false));
  }, [selectedStoreId, token]);

  // Load leaderboard
  const loadLeaderboard = useCallback(() => {
    if (!selectedStoreId || !token) return;
    setLoadingLeaderboard(true);
    fetch(`${BASE}/api/game/leaderboard?storeId=${selectedStoreId}`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setLeaderboard(data.leaderboard || []))
      .catch(() => {})
      .finally(() => setLoadingLeaderboard(false));
  }, [selectedStoreId, token]);

  // Auto-refresh leaderboard every 30 s when on that tab
  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboard();
      refreshTimerRef.current = setInterval(loadLeaderboard, 30_000);
    } else {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [activeTab, loadLeaderboard]);

  const addTier = () => {
    setTiers((prev) => [...prev, emptyTier(prev.length)]);
  };

  const removeTier = (index: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== index).map((t, i) => ({ ...t, sortOrder: i })));
  };

  const updateTier = (index: number, patch: Partial<Tier>) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const moveTier = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tiers.length) return;
    setTiers((prev) => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex]!, arr[index]!];
      return arr.map((t, i) => ({ ...t, sortOrder: i }));
    });
  };

  const saveTiers = async () => {
    if (!selectedStoreId) return;
    setSavingTiers(true);
    try {
      const r = await fetch(`${BASE}/api/game/tiers`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, tiers }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.message ?? 'Save failed');
      }
      const data = await r.json();
      setTiers(data.tiers || []);
      toast({ title: 'Tiers saved' });
    } catch (err: any) {
      toast({ title: 'Failed to save tiers', description: err.message, variant: 'destructive' });
    } finally {
      setSavingTiers(false);
    }
  };

  const discountLabel = (tier: Tier) => {
    if (tier.discountType === 'percent') return `${tier.discountValue}% off`;
    return `$${(tier.discountValue / 100).toFixed(2)} off`;
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Gamepad2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Game Popup</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Let customers earn discount codes by playing Snake on your store
              </p>
            </div>
          </div>

          <ShopifyTargetStoreSelector
            stores={stores}
            value={selectedStoreId}
            onChange={setSelectedStoreId}
            label="Shopify store"
            className="w-full sm:w-56"
          />
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {[
            { key: 'tiers',       label: 'Score Tiers',  icon: Gamepad2 },
            { key: 'leaderboard', label: 'Leaderboard',  icon: Trophy },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === key
                  ? 'border-emerald-500 text-emerald-500'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── TIERS TAB ──────────────────────────────────────── */}
        {activeTab === 'tiers' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Define score ranges and the discount each player earns. Lower tiers should have smaller score thresholds.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={addTier}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Tier
                </button>
                <button
                  onClick={saveTiers}
                  disabled={savingTiers}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-60"
                >
                  {savingTiers ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Tiers
                </button>
              </div>
            </div>

            {loadingTiers ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
              </div>
            ) : tiers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-card border border-border border-dashed rounded-xl">
                <Gamepad2 className="w-10 h-10 text-muted-foreground mb-3 opacity-40" />
                <p className="text-sm font-medium text-foreground">No tiers configured yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Add a tier to start rewarding players with discount codes.
                </p>
                <button
                  onClick={addTier}
                  className="mt-4 flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add First Tier
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Column labels */}
                <div className="grid grid-cols-[32px_1fr_100px_100px_130px_120px_36px] gap-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  <span />
                  <span>Tier Name</span>
                  <span>Min Score</span>
                  <span>Max Score</span>
                  <span>Discount Type</span>
                  <span>Value</span>
                  <span />
                </div>

                {tiers.map((tier, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[32px_1fr_100px_100px_130px_120px_36px] gap-3 items-center bg-card border border-border rounded-xl px-4 py-3"
                  >
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => moveTier(i, -1)}
                        disabled={i === 0}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
                        title="Move up"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => moveTier(i, 1)}
                        disabled={i === tiers.length - 1}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
                        title="Move down"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Tier name */}
                    <input
                      value={tier.tierName}
                      onChange={(e) => updateTier(i, { tierName: e.target.value })}
                      placeholder="e.g. Bronze"
                      className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />

                    {/* Min score */}
                    <input
                      type="number"
                      min={0}
                      value={tier.minScore}
                      onChange={(e) => updateTier(i, { minScore: parseInt(e.target.value, 10) || 0 })}
                      className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />

                    {/* Max score */}
                    <input
                      type="number"
                      min={0}
                      value={tier.maxScore}
                      onChange={(e) => updateTier(i, { maxScore: parseInt(e.target.value, 10) || 0 })}
                      className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />

                    {/* Discount type */}
                    <select
                      value={tier.discountType}
                      onChange={(e) => updateTier(i, { discountType: e.target.value as 'fixed' | 'percent' })}
                      className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="percent">Percentage (%)</option>
                      <option value="fixed">Fixed ($)</option>
                    </select>

                    {/* Value */}
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">
                        {tier.discountType === 'percent' ? '%' : '$'}
                      </span>
                      <input
                        type="number"
                        min={0}
                        value={tier.discountType === 'fixed' ? (tier.discountValue / 100).toFixed(2) : tier.discountValue}
                        onChange={(e) => {
                          const raw = parseFloat(e.target.value) || 0;
                          updateTier(i, { discountValue: tier.discountType === 'fixed' ? Math.round(raw * 100) : Math.round(raw) });
                        }}
                        step={tier.discountType === 'fixed' ? '0.01' : '1'}
                        className="w-full bg-background border border-border rounded-lg pl-7 pr-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                    </div>

                    {/* Remove */}
                    <button
                      onClick={() => removeTier(i)}
                      className="text-muted-foreground hover:text-rose-500 transition-colors"
                      title="Remove tier"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Preview */}
                <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Preview</p>
                  <div className="flex flex-wrap gap-2">
                    {tiers.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-lg text-xs">
                        <span className="font-semibold text-foreground">{t.tierName || `Tier ${i + 1}`}</span>
                        <span className="text-muted-foreground">{t.minScore}–{t.maxScore} pts</span>
                        <span className="text-emerald-500 font-medium">{discountLabel(t)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── LEADERBOARD TAB ────────────────────────────────── */}
        {activeTab === 'leaderboard' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                All-time top scores. Each player's personal best is shown. Refreshes every 30 seconds.
              </p>
              <button
                onClick={loadLeaderboard}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingLeaderboard ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadingLeaderboard && leaderboard.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-card border border-border rounded-xl">
                <Trophy className="w-10 h-10 text-muted-foreground mb-3 opacity-40" />
                <p className="text-sm font-medium text-foreground">No scores yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Scores will appear here once customers play the game on your store.
                </p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide w-12">Rank</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Player</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Best Score</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Codes Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry, i) => (
                      <tr
                        key={i}
                        className={`border-b border-border last:border-0 transition-colors ${
                          i % 2 === 0 ? 'hover:bg-muted/20' : 'bg-muted/10 hover:bg-muted/20'
                        }`}
                      >
                        <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                          {i === 0 ? (
                            <span className="text-amber-500 font-bold">🥇</span>
                          ) : i === 1 ? (
                            <span className="text-slate-400 font-bold">🥈</span>
                          ) : i === 2 ? (
                            <span className="text-amber-700 font-bold">🥉</span>
                          ) : (
                            <span className="opacity-60">#{i + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground">{entry.firstName}</td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-emerald-500">
                          {entry.score.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {entry.claimCount > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-500">
                              {entry.claimCount} {entry.claimCount === 1 ? 'code' : 'codes'}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
